import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight04Icon, Files02Icon } from '@hugeicons-pro/core-bulk-rounded/index'
import { Archive01Icon, FileUploadIcon, JoinBevelIcon } from '@hugeicons-pro/core-twotone-rounded/index'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Box, Button, TextField, ThemeProvider, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { alpha, useTheme } from '@mui/material/styles'
import { useSnackbar } from 'notistack'

import { useGetCustomerQuery } from '@/app/services/customer'
import { useCreateOrderMaterialListMutation } from '@/app/services/materials'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectOrganisation, selectOrganisationId, selectPaidFeatures } from '@/app/slices/organisationSlice'
import AddItemCard from '@/common/components/AddItemCards/AddItemCard'
import AppToolbar from '@/common/components/AppBar/AppToolbar'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { theme as darkTheme } from '@/common/themes/DarkTheme'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'

import Add3DPartsDialog from '../order/components/common/Add3DPartsDialog'
import AddFromPartLibrary from '../order/components/common/AddFromPartLibrary'
import AddOrderItemsDialog from '../order/components/common/AddOrderItemsDialog'
import AddPDFPartsDialog from '../order/components/common/AddPDFParts'
import AddQuickPartDialog from '../order/components/common/AddQuickPartDialog'

const classes = {
    container: {
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.grey[300]}`,
        paddingInline: 3,
        paddingBlock: 4,
        minHeight: 'calc(100vh - 158px)',
    },
    mainContent: {
        maxWidth: '600px',
        gap: 5,
    },

    draftIdContainer: {
        gap: 0.75,
    },
    draftIdInputContainer: {
        gap: 1.5,
    },
    goToDraftButton: {
        flex: '1 0 auto',
    },
    fileListContainer: {
        overflow: 'hidden',
    },
    fileListWrapper: {
        marginBottom: 2,
    },
    filesToUpload: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        marginBottom: 1.5,
    },
    fileItem: {
        px: 1,
        py: 0.5,
        gap: 1,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderRadius: 0.5,
    },
    smallText: {
        fontSize: '0.75rem',
    },
}

const Home = () => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { show3D, showOrderHistory, showPDF, showPartLibrary, showTube } = useToolBoxTreatments()

    const { isAuthenticated, login, register } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const paidFeatures = useSelector(selectPaidFeatures)
    const currentCustomerId = useSelector(selectCustomerId)

    const [orderId, setOrderId] = useState('')
    const [interfaceToShow, setInterfaceToShow] = useState(null)
    const [is3DTube, setIs3DTube] = useState(false)

    const isGuestModeEnabled = storeSettings?.webStoreIsPublic
    const isPartLibraryEnabled = isAuthenticated && showPartLibrary && paidFeatures?.hasPartLibrary
    const isUnfoldTubeEnabled = isAuthenticated && showTube && paidFeatures?.hasUnfoldTube
    const is3dEnabled = show3D && paidFeatures?.has3d
    const isPDFUploadEnabled = showPDF && paidFeatures?.hasPdfUpload

    const { data: currentCustomer } = useGetCustomerQuery(
        { organisationId, customerId: currentCustomerId },
        { skip: !isAuthenticated && !currentCustomerId }
    )

    const [createOrderMaterialList] = useCreateOrderMaterialListMutation()

    const handleDraftIdChange = (e) => {
        setOrderId(e.target.value)
    }

    const handleGoToDraft = () => {
        if (orderId) {
            navigate(`orders/${orderId}`)
        }
    }

    const createMaterialList = async (organisationId, urlParams) => {
        try {
            await createOrderMaterialList({ organisationId, location: urlParams }).unwrap()
        } catch (error) {
            console.error(error)
        }
    }

    const resolvedDarkTheme = useMemo(
        () => (StoreThemeOverride ? StoreThemeOverride(darkTheme, storeSettings) : darkTheme),
        [StoreThemeOverride, darkTheme, storeSettings]
    )

    const resetInterface = () => {
        setInterfaceToShow(null)
        setIs3DTube(false)
    }

    const handleAdd2dPartsClick = () => {
        setInterfaceToShow('2d')
    }

    const handleAddFromQuickPartClick = () => {
        setInterfaceToShow('quickpart')
    }

    const handleAdd3dPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Unfold3d')
        setInterfaceToShow('3d-tube')
    }, [organisationId])

    const handleAddRotaryPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Unfold3d')
        setIs3DTube(true)
        setInterfaceToShow('3d-tube')
    }, [organisationId])

    const handleAddPdfPartsClick = useCallback(async () => {
        await createMaterialList(organisationId, 'Pdf2Quote')
        setInterfaceToShow('pdf')
    }, [organisationId])

    const handleAddFromPartsLibraryClick = () => {
        try {
            setInterfaceToShow('partlibrary')
        } catch (err) {
            enqueueSnackbar(t('$t(An error occurred), failed to add parts from the library.'), {
                variant: 'error',
            })
        }
    }

    return (
        <>
            <AppToolbar />
            <Grid
                component="section"
                size={{
                    xs: 12,
                }}
                sx={classes.container}
                container
            >
                <Grid
                    size={{
                        xs: 12,
                    }}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: 1,
                        gap: 6,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            textAlign="center"
                            variant="h2"
                        >
                            {storeSettings?.webStoreHeadingParagraph || t('Welcome to the store')}
                        </Typography>
                        <Typography
                            align="center"
                            variant="h5"
                        >
                            {storeSettings?.webStoreSubheadingParagraph ||
                                t('From upload to purchase in a matter of minutes')}
                        </Typography>
                    </Box>

                    {!isAuthenticated ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 4 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: 2,
                                    paddingTop: 3.5,
                                    flexDirection: 'column',
                                    gap: 2,
                                    width: 300,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Button
                                    color="primary"
                                    size="large"
                                    variant="contained"
                                    fullWidth
                                    onClick={() => login({ org_code: storeSettings?.kindeOrganisationId })}
                                >
                                    {t('Log in')}
                                </Button>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">{t("Don't have an account?")}</Typography>
                                    <Button
                                        color="secondary"
                                        size="small"
                                        variant="text"
                                        onClick={() => register({ org_code: storeSettings?.kindeOrganisationId })}
                                    >
                                        {t('Register here')}
                                    </Button>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    width: 500,
                                }}
                            >
                                <form
                                    action={handleGoToDraft}
                                    id="go-to-draft-form"
                                >
                                    <Typography
                                        sx={{ marginBottom: 1 }}
                                        variant="body2"
                                    >
                                        {t("Don't have an account? Continue with an order ID")}
                                    </Typography>

                                    <TextField
                                        helperText={t(
                                            'Enter the order ID you received via email to view or continue your saved order.'
                                        )}
                                        label="Order ID"
                                        size="small"
                                        value={orderId}
                                        variant="outlined"
                                        fullWidth
                                        onChange={handleDraftIdChange}
                                    />
                                </form>

                                <Button
                                    color="secondary"
                                    disabled={!orderId}
                                    endIcon={<HugeiconsIcon icon={ArrowRight04Icon} />}
                                    form="go-to-draft-form"
                                    size="medium"
                                    type="submit"
                                    variant="contained"
                                >
                                    {t('Go to Order')}
                                </Button>
                            </Box>
                        </Box>
                    ) : showOrderHistory ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 3,
                            }}
                        >
                            <Button
                                color="primary"
                                size="medium"
                                startIcon={<HugeiconsIcon icon={Files02Icon} />}
                                variant="contained"
                                onClick={() => navigate('/my-orders')}
                            >
                                {t('Order History')}
                            </Button>

                            <Typography variant="body2">{t('or')}</Typography>
                        </Box>
                    ) : null}
                </Grid>

                {isAuthenticated || isGuestModeEnabled ? (
                    <Grid
                        size={{
                            xs: 12,
                        }}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            width: 1,
                            gap: 3,
                        }}
                    >
                        <Box>
                            {!isAuthenticated && isGuestModeEnabled ? (
                                <Typography
                                    sx={{ marginTop: 4 }}
                                    textAlign="center"
                                    variant="body2"
                                >
                                    {t('or')}
                                </Typography>
                            ) : null}
                            <Typography variant="body1">{t('Select an option to start a new order')}</Typography>
                        </Box>

                        <Grid
                            size={12}
                            sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}
                        >
                            <AddItemCard
                                buttonText="Upload files"
                                helperText="Any design uploaded is secure, and you retain 100% of the intellectual property rights."
                                icon={
                                    <HugeiconsIcon
                                        color={theme.palette.secondary.main}
                                        icon={FileUploadIcon}
                                        size={64}
                                    />
                                }
                                title="Upload your DXF or DWG files to get started"
                                onButtonClick={handleAdd2dPartsClick}
                            />
                            <AddItemCard
                                buttonText="Create a Quick part"
                                helperText="Quickly create standard parts such as base plates, flanges and bracketry from a library of templates, and customise them to your needs."
                                icon={
                                    <HugeiconsIcon
                                        color={theme.palette.secondary.main}
                                        icon={JoinBevelIcon}
                                        size={64}
                                    />
                                }
                                title="Don’t have a CAD file but need something standard?"
                                onButtonClick={handleAddFromQuickPartClick}
                            />

                            {isPartLibraryEnabled ? (
                                <AddItemCard
                                    buttonText="Browse your part library"
                                    helperText="In your part library, you can find parts from past orders and quickly add them to a new order."
                                    icon={
                                        <HugeiconsIcon
                                            color={theme.palette.secondary.main}
                                            icon={Archive01Icon}
                                            size={64}
                                        />
                                    }
                                    title="Want to reorder some parts?"
                                    onButtonClick={handleAddFromPartsLibraryClick}
                                />
                            ) : null}
                        </Grid>

                        <Grid
                            size={12}
                            sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}
                        >
                            {is3dEnabled ? (
                                <AddItemCard
                                    buttonText="Unfold 3D models"
                                    title="Upload a 3D model and we'll extract the sheet metal components"
                                    onButtonClick={handleAdd3dPartsClick}
                                />
                            ) : null}

                            {isUnfoldTubeEnabled ? (
                                <AddItemCard
                                    buttonText="Upload tube models"
                                    title="Upload rolled hollow section tube files"
                                    onButtonClick={handleAddRotaryPartsClick}
                                />
                            ) : null}

                            {isPDFUploadEnabled ? (
                                <AddItemCard
                                    buttonText="Extract from PDF"
                                    title="Upload a vector PDF and select the parts to cut"
                                    onButtonClick={handleAddPdfPartsClick}
                                />
                            ) : null}
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>

            {interfaceToShow === '2d' ? (
                <AddOrderItemsDialog
                    open={interfaceToShow === '2d'}
                    onClose={resetInterface}
                />
            ) : null}

            <ThemeProvider theme={resolvedDarkTheme}>
                {interfaceToShow === 'quickpart' ? (
                    <AddQuickPartDialog
                        open={interfaceToShow === 'quickpart'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === '3d-tube' ? (
                    <Add3DPartsDialog
                        is3DTube={is3DTube}
                        open={interfaceToShow === '3d-tube'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === 'pdf' ? (
                    <AddPDFPartsDialog
                        open={interfaceToShow === 'pdf'}
                        onClose={resetInterface}
                    />
                ) : null}

                {interfaceToShow === 'partlibrary' && isAuthenticated && currentCustomer ? (
                    <AddFromPartLibrary
                        customer={currentCustomer}
                        open={interfaceToShow === 'partlibrary'}
                        onClose={resetInterface}
                    />
                ) : null}
            </ThemeProvider>
        </>
    )
}

export default Home
